// VsGongPage.js — SEO landing page targeting "Gong alternative Australia"
// and similar high-intent queries. Public, no auth. Anchors the direct
// comparison → free audit → trial CTA flow.
import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, ArrowRight, Sparkles, DollarSign, MapPin, Layers, Zap, ShieldCheck } from 'lucide-react';

const COMPARISON_ROWS = [
  { label: 'Per-user cost (AUD equivalent)', gong: 'A$220-300 / user / mo', vf: 'A$49-149 / user / mo', highlight: true },
  { label: 'Setup fee', gong: 'US$7,500 - $65,000', vf: 'A$3,500 - $11,900' },
  { label: 'Minimum seats', gong: '~50 seats typical', vf: '3 seats · A$299/mo min' },
  { label: 'Contract length', gong: '2-3 year lock-in typical', vf: 'Month-to-month · annual optional' },
  { label: 'Pricing transparency', gong: 'Custom quote only', vf: 'Published on /pricing' },
  { label: 'Roles graded', gong: 'Sales only', vf: 'Sales · Support · Admin · Accounts · Management' },
  { label: 'Native UCaaS integration', gong: 'External · needs config', vf: 'Native to Yabbit UCaaS' },
  { label: 'Australian data residency', gong: 'US-hosted default', vf: 'AU-hosted · tenant-isolated' },
  { label: 'Governance-independent escalation', gong: 'Score-based only', vf: 'Independent + score-based' },
  { label: 'Multi-tenant / white-label', gong: 'Not offered', vf: 'Yes · 50% reseller margin' },
  { label: 'Time to deploy', gong: '6-12 weeks typical', vf: '4 weeks standard' },
  { label: 'AU-based support', gong: 'US support hours', vf: 'AU business hours + email' },
];

const Chip = ({ ok, children }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${ok ? 'bg-teal-500/15 text-teal-300' : 'bg-red-500/15 text-red-300'}`}>
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    {children}
  </span>
);

const VsGongPage = () => (
  <div className="min-h-screen bg-[#0A0F1C]" data-testid="vs-gong-page">
    {/* Header */}
    <header className="border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-teal-500 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="text-white font-bold">Yabbit VoiceFabric</span>
        </Link>
        <div className="flex items-center gap-4 text-sm">
          <Link to="/pricing" className="text-white/60 hover:text-white">Pricing</Link>
          <Link to="/login" className="text-teal-300 hover:text-teal-200 font-semibold" data-testid="vs-gong-signin">Sign in →</Link>
        </div>
      </div>
    </header>

    {/* Hero */}
    <section className="max-w-4xl mx-auto px-6 pt-20 pb-12 text-center">
      <p className="text-teal-300 text-xs font-semibold uppercase tracking-wider mb-3">Gong Alternative for Australian Teams</p>
      <h1 className="text-5xl sm:text-6xl font-black text-white leading-tight tracking-tight">
        Gong is priced for US enterprise.<br /><span className="text-teal-400">We&apos;re not.</span>
      </h1>
      <p className="text-white/70 text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
        Yabbit VoiceFabric grades <strong className="text-white">100% of your calls</strong> across sales, support, admin, accounts and management — priced from
        <strong className="text-teal-300"> A$49/user/month</strong>, with a 4-week deployment on Yabbit UCaaS. No US$95,000 pilot contracts.
      </p>
      <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          data-testid="vs-gong-pricing-cta"
        >
          See our pricing
          <ArrowRight className="w-4 h-4" />
        </Link>
        <Link to="/free-audit"
          className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          data-testid="vs-gong-audit-cta"
        >
          Free 3-call audit
        </Link>
      </div>
    </section>

    {/* At-a-glance callouts */}
    <section className="max-w-5xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-4">
      {[
        { icon: DollarSign, title: '4-5× cheaper', body: 'A$49-149/user/mo vs A$220-300/user/mo Gong equivalent' },
        { icon: Layers, title: '5× the roles', body: 'Sales, support, admin, accounts + management — not just sales' },
        { icon: MapPin, title: 'Built for AU SMB', body: '3-seat minimum, month-to-month, published pricing' },
      ].map((c) => (
        <div key={c.title} className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <c.icon className="w-6 h-6 text-teal-400 mb-3" />
          <h3 className="text-white font-bold text-lg">{c.title}</h3>
          <p className="text-white/60 text-sm mt-1">{c.body}</p>
        </div>
      ))}
    </section>

    {/* Comparison table */}
    <section className="max-w-6xl mx-auto px-6 py-16" data-testid="vs-gong-comparison">
      <div className="text-center mb-10">
        <h2 className="text-3xl sm:text-4xl font-black text-white">Side-by-side</h2>
        <p className="text-white/50 text-sm mt-3">Pricing as of Feb 2026, sourced from published vendor pages + analyst reports.</p>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-white/60 text-xs uppercase tracking-wider bg-white/5">
              <th className="px-4 py-3 text-left">Feature</th>
              <th className="px-4 py-3 text-left">Gong</th>
              <th className="px-4 py-3 text-left text-teal-300">Yabbit VoiceFabric</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {COMPARISON_ROWS.map((r) => (
              <tr key={r.label} className={r.highlight ? 'bg-teal-500/5' : ''}>
                <td className="px-4 py-3 text-white/80 font-medium">{r.label}</td>
                <td className="px-4 py-3 text-white/50">
                  <Chip ok={false}>{r.gong}</Chip>
                </td>
                <td className="px-4 py-3">
                  <Chip ok>{r.vf}</Chip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-white/30 text-[11px] mt-4 italic text-center">
        Gong figures aggregated from public analyst reports (Vendr, Sybill, GetFairview). AUD conversions at 1 USD ≈ 1.55 AUD.
        Comparisons are best-effort accurate as of Feb 2026 and do not represent legal advice.
      </p>
    </section>

    {/* The three real reasons */}
    <section className="max-w-4xl mx-auto px-6 py-16 space-y-10">
      <div className="text-center mb-6">
        <h2 className="text-3xl sm:text-4xl font-black text-white">Three reasons Australian teams switch</h2>
      </div>

      {[
        {
          n: '01',
          title: 'The economics work for actual Australian SMB',
          body: 'Gong pilots typically start at A$60,000+ for a 30-seat sales team on a 2-year commitment. That\'s A$1,000/user/year even before setup fees. Yabbit VoiceFabric Coach at A$89/user/mo × 30 seats = A$32,040/year — with no lock-in and no US-currency risk.',
        },
        {
          n: '02',
          title: 'One platform for the whole business — not just sales',
          body: 'Gong grades sales calls. VoiceFabric grades sales, customer support, admin & reception, accounts & billing, AND management/coaching conversations. Same platform, one tenant, one price. Perfect for consultative businesses where the sales/service line is blurry.',
        },
        {
          n: '03',
          title: 'Native to Yabbit UCaaS — no data hop',
          body: 'Gong sits on top of your phone system with webhooks, integrations, and a support ticket every time your ACD changes. VoiceFabric IS your Yabbit UCaaS extension — same auth, same tenant, same audit trail. Deployed in 4 weeks, not 4 months.',
        },
      ].map((r) => (
        <div key={r.n} className="flex gap-6 items-start">
          <div className="text-6xl font-black text-teal-500/40 leading-none">{r.n}</div>
          <div>
            <h3 className="text-white font-bold text-xl mb-2">{r.title}</h3>
            <p className="text-white/70 leading-relaxed">{r.body}</p>
          </div>
        </div>
      ))}
    </section>

    {/* When Gong IS the right choice — credibility play */}
    <section className="max-w-3xl mx-auto px-6 py-12">
      <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-6">
        <h3 className="text-amber-300 font-bold text-lg mb-3">Honest answer: when Gong is the right choice</h3>
        <p className="text-white/70 leading-relaxed">
          If you&apos;re a <strong className="text-white">300+ seat US SaaS sales team</strong> running MEDDIC in Salesforce with a full RevOps function, Gong is the market leader for a reason —
          deep Salesforce integration, mature deal-intelligence tooling, and category-defining call analytics.
          For that customer profile, Gong is legitimately the right platform.
        </p>
        <p className="text-white/70 leading-relaxed mt-3">
          For the <strong className="text-white">20-100 seat Australian phone-heavy business</strong>, Gong is overkill, over-priced, and doesn&apos;t cover half your team&apos;s call volume.
          That&apos;s the customer VoiceFabric exists for.
        </p>
      </div>
    </section>

    {/* FAQ */}
    <section className="max-w-3xl mx-auto px-6 py-16" data-testid="vs-gong-faq">
      <h2 className="text-3xl font-black text-white text-center mb-10">FAQ</h2>
      <div className="space-y-6">
        {[
          {
            q: 'Does VoiceFabric integrate with Salesforce / HubSpot?',
            a: 'Yes — the Intelligence tier includes CRM webhook integration with Salesforce, HubSpot, and Pipedrive. Deal outcomes flow back to VoiceFabric for attribution, and call summaries push to the CRM contact record.',
          },
          {
            q: 'What if we don\'t use Yabbit UCaaS?',
            a: 'VoiceFabric works best natively on Yabbit — that\'s the 4-week deploy. If you\'re on a different UCaaS (RingCentral, 8x8, Aircall), we support inbound via webhook integration on the Enterprise tier. Migration to Yabbit UCaaS typically pays for itself in the first year via bundled pricing.',
          },
          {
            q: 'How is the grading accurate?',
            a: 'We use Claude / GPT-class LLMs against your customised knowledge base, calibrated nightly against 15 known-good transcripts. Accuracy is verifiably in the 85-92% range on well-configured KBs — and every score is auditable back to the exact criterion, weight, and transcript passage.',
          },
          {
            q: 'What about employee privacy / Fair Work concerns?',
            a: 'Every call is already being recorded by your Yabbit UCaaS. VoiceFabric adds coaching to that recording — the rep sees their own dashboard, their own score, their own next focus. Coaching is FOR them, not surveillance OF them. We ship an "employee notify" template so HR can roll out transparently.',
          },
          {
            q: 'Can we try before we buy?',
            a: 'Two ways: (1) Free 3-call audit — upload three recordings, we grade them against your KB, you see the output before signing anything. (2) 14-day free trial on the Coach tier, no credit card required.',
          },
        ].map((f, i) => (
          <details key={i} className="bg-white/5 border border-white/10 rounded-xl p-5 group">
            <summary className="cursor-pointer text-white font-semibold flex items-center justify-between">
              {f.q}
              <ArrowRight className="w-4 h-4 text-teal-400 group-open:rotate-90 transition-transform" />
            </summary>
            <p className="text-white/60 text-sm mt-3 leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </section>

    {/* Bottom CTA */}
    <section className="max-w-4xl mx-auto px-6 py-16 text-center">
      <div className="bg-gradient-to-br from-teal-500/10 via-teal-500/5 to-transparent border border-teal-500/30 rounded-3xl p-10">
        <h2 className="text-3xl sm:text-4xl font-black text-white leading-tight">
          Stop being quoted for someone else&apos;s market.
        </h2>
        <p className="text-white/70 text-lg mt-4 max-w-xl mx-auto">
          Upload three of your recordings. We&apos;ll grade them against your knowledge base and show you what&apos;s hiding in the 97% of calls you never review.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <Link to="/free-audit"
            className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-bold px-8 py-4 rounded-lg text-lg transition-colors"
            data-testid="vs-gong-final-cta"
          >
            Get my free audit
            <ArrowRight className="w-5 h-5" />
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 text-teal-300 hover:text-teal-200 font-semibold px-4 py-4"
          >
            See pricing →
          </Link>
        </div>
      </div>
    </section>

    {/* Trust bar */}
    <section className="max-w-5xl mx-auto px-6 py-16 border-t border-white/5">
      <div className="grid md:grid-cols-3 gap-8 text-center">
        <div>
          <ShieldCheck className="w-8 h-8 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold">Australian data residency</h3>
          <p className="text-white/50 text-sm mt-1">AU-hosted · tenant-isolated · Privacy Act 2026 ready</p>
        </div>
        <div>
          <Zap className="w-8 h-8 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold">Live in 4 weeks</h3>
          <p className="text-white/50 text-sm mt-1">Onboarding call · KB workshop · baseline scoring by week 3</p>
        </div>
        <div>
          <Layers className="w-8 h-8 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold">5-role grading</h3>
          <p className="text-white/50 text-sm mt-1">Sales, support, admin, accounts, management — one platform</p>
        </div>
      </div>
    </section>

    <footer className="border-t border-white/5 py-8 text-center text-white/30 text-xs">
      © 2026 Yabbit VoiceFabric · yabbit.com.au · <Link to="/pricing" className="hover:text-white/50">Pricing</Link> · <Link to="/" className="hover:text-white/50">Home</Link>
    </footer>
  </div>
);

export default VsGongPage;
